import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  InputLabel,
  MenuItem,
  Popover,
  Select,
} from '@material-ui/core';
import { Close, FilterList, Search } from '@material-ui/icons';
import React, { useRef, useState } from 'react';
import { Badge, FormControl, IconButton, InputAdornment, makeStyles, TextField, Theme } from '../../../index';
import { FilterOptionWithValueType } from '../models/transferList.types';
import { FilterMenuProps } from '../models/transferListProps.types';
import { createFilterWithValueType } from '../utils/transferList.utils';

const useStyles = makeStyles((theme: Theme) => ({
  filterContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  filterMenu: {
    width: 310,
  },
  filterItem: {
    marginBottom: theme.spacing(3),
  },
  filterActions: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  searchInput: {
    width: 200,
    marginLeft: theme.spacing(1.5),
  },
  closeIcon: {
    cursor: 'pointer',
  },
}));

export const FilterMenu = ({
  activeSearchFilter,
  setSearchFilter,
  setCurrentFilters,
  showFilter,
  showSearchbar,
  searchbarPlaceholder,
  filterOptions,
  currentFilters = [],
  resetButtonValue = 'Reset',
}: FilterMenuProps): JSX.Element => {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState<Element | null>(null);
  const popoverRef = useRef(null);
  const filterObject = createFilterWithValueType(filterOptions);

  const handleClick = () => {
    setAnchorEl(popoverRef.current);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  return (
    <div className={classes.filterContainer} data-testid={'FilterMenu-component'}>
      {showFilter && filterObject ? (
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        <IconButton
          ref={popoverRef}
          color={'primary'}
          data-testid={'show-filters-component'}
          aria-label={2}
          component="span"
          onClick={handleClick}
        >
          <Badge badgeContent={'test'} color={'primary'} invisible={true}>
            <FilterList />
          </Badge>
        </IconButton>
      ) : null}

      {showSearchbar ? (
        <FormControl className={classes.searchInput}>
          <TextField
            id="search-input"
            data-testid={'search-input'}
            placeholder={searchbarPlaceholder}
            value={activeSearchFilter}
            onChange={(event: React.ChangeEvent<any>) => setSearchFilter(event.currentTarget.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search color="primary" fontSize={'small'} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment className={classes.closeIcon} position="end" onClick={() => setSearchFilter('')}>
                  <Close fontSize={'small'} />
                </InputAdornment>
              ),
            }}
          />
        </FormControl>
      ) : null}

      {showFilter && filterObject ? (
        <Popover
          id={id}
          data-testid={'filter-menu'}
          anchorEl={anchorEl}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          keepMounted
          open={open}
          onClose={handleClose}
        >
          <Card className={classes.filterMenu}>
            <CardHeader title="filters" />
            <CardContent>
              {filterObject?.map((filter: FilterOptionWithValueType, index: number) => {
                return (
                  <FormControl key={index} fullWidth className={classes.filterItem}>
                    <InputLabel id={`input-label-${filter.label}`}>{filter.label}</InputLabel>
                    <Select
                      labelId={`filter-label-${filter.label}`}
                      id={`filter-id-${filter.label}`}
                      data-testid={'filter-select'}
                      value={filter.value}
                      fullWidth
                      onChange={(event) =>
                        setCurrentFilters([
                          ...currentFilters,
                          { value: (event.target?.value as string) || '', label: filter.label },
                        ])
                      }
                    >
                      {filter.options.map((option: string, index: number) => {
                        return (
                          <MenuItem key={index} value={option}>
                            {option === 'NONE' ? '-' : option}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                );
              })}
            </CardContent>

            <CardActions className={classes.filterActions}>
              <Button variant="outlined" data-testid={'reset-button'} onClick={() => setCurrentFilters([])}>
                {resetButtonValue}
              </Button>
            </CardActions>
          </Card>
        </Popover>
      ) : null}
    </div>
  );
};
